import React, { useContext, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useOffer, useTrade } from '../../../../stores/hooks';
import {
  OfferContext, ProfileContext, ViewContext, ViewModalActionTypes,
} from '../../../../stores/contexts';

import { AppElement, AppFC, TradeInterface } from '../../../../interfaces';
import { offerOptions, OfferStatusTypes, OfferTypes } from '../../../../constants';

import { DraftOfferCard, MyOfferBox } from '../../../Molecules';
import { Loader, Switch } from '../../../Atoms';
import { ScrollLoadOffers } from '../../../Organisms';
import { OfferParamsType } from '../../../../services/httpRequest';

export const MyOffers: AppFC = (): AppElement => {
  const [isLoading, setIsLoading] = useState(false);
  const [offerIsDeleting, setOfferIsDeleting] = useState(false);
  const [currentOfferType, setCurrentOfferType] = useState(OfferTypes.All);
  const [trades, setTrades] = useState<TradeInterface[]>([]);

  const { profileState: { id: userId } } = useContext(ProfileContext);
  const {
    offerState: {
      publishedOffers,
      unpublishedOffers,
      draftOffers,
    },
  } = useContext(OfferContext);
  const { updateViewModal } = useContext(ViewContext);

  const {
    getOffers,
    deleteOffer,
  } = useOffer();
  const { getTrades } = useTrade();

  const getOfferType = () => {
    switch (currentOfferType) {
      case OfferTypes.Buy:
        return OfferTypes.Sell;
      case OfferTypes.Sell:
        return OfferTypes.Buy;
      default:
        return currentOfferType;
    }
  };

  useEffect(() => {
    if (!offerIsDeleting) {
      setIsLoading(true);
      const paramsForPublished = currentOfferType === OfferTypes.All ? {
        OfferStatus: OfferStatusTypes.Open,
        UserId: userId,
      } : {
        OfferType: getOfferType(),
        OfferStatus: OfferStatusTypes.Open,
        UserId: userId,
      };

      const paramsForUnpublished = currentOfferType === OfferTypes.All ? {
        OfferStatus: OfferStatusTypes.Unpublished,
        UserId: userId,
      } : {
        OfferType: getOfferType(),
        OfferStatus: OfferStatusTypes.Unpublished,
        UserId: userId,
      };

      const paramsForDraft = currentOfferType === OfferTypes.All ? {
        OfferStatus: OfferStatusTypes.Draft,
        UserId: userId,
      } : {
        OfferType: getOfferType(),
        OfferStatus: OfferStatusTypes.Draft,
        UserId: userId,
      };

      Promise.all([
        getOffers(paramsForPublished),
        getOffers(paramsForDraft),
        getOffers(paramsForUnpublished),
        getTrades(),
      ])
        .then((res) => {
          setTrades(res[3]);
          setIsLoading(false);
        })
        .catch((reason) => {
          console.log('reason', reason);
          setIsLoading(false);
        });
    }
  }, [currentOfferType, offerIsDeleting]);

  const handleDeleteClick = (id: string) => {
    setOfferIsDeleting(true);
    deleteOffer(id)
      .then(() => {
        updateViewModal({
          type: ViewModalActionTypes.SuccessModal,
          payload: {
            title: 'Success',
            description: 'Offer deleted successfully.',
          },
        });
        setOfferIsDeleting(false);
      })
      .catch(() => {
        setOfferIsDeleting(false);
      });
  };

  const tradeOfferIds = trades.map((trade) => trade.offerId);

  // filter params for infinite scroll load-more (same pattern as Offers page)
  const filterParams: OfferParamsType = currentOfferType === OfferTypes.All ? {
    OfferStatus: OfferStatusTypes.Open,
    UserId: userId,
  } : {
    OfferType: getOfferType(),
    OfferStatus: OfferStatusTypes.Open,
    UserId: userId,
  };

  return (
    <>
      <Helmet>
        <title>Cryptolocally: My Offers</title>
      </Helmet>
      <div className="flex flex-col self-center gap-10 w-full py-20 px-[3%] md:px-[3%] xl:px-[8%]">
        <div>
          <h3>My offers</h3>
        </div>

        <Switch
          name="offerType"
          items={offerOptions}
          itemClass="font-semibold"
          value={currentOfferType}
          setValue={(value) => {
            setCurrentOfferType(value as OfferTypes);
          }}
          height="h-12"
        />

        {isLoading ? <Loader className="self-center justify-self-center" /> : (
          <div className="flex flex-col gap-12">
            <h5>Published Offers</h5>
            <div>
              <ScrollLoadOffers filterParams={filterParams} status={OfferStatusTypes.Open} triggerAtCenter>
                <div>
                  {publishedOffers.map((offer) => (
                    <MyOfferBox
                      key={offer.id}
                      offer={offer}
                      tradeOfferIds={tradeOfferIds}
                      onDeleteClick={handleDeleteClick}
                      offerStatus={OfferStatusTypes.Open}
                    />
                  ))}
                </div>
              </ScrollLoadOffers>
            </div>
            {unpublishedOffers.length > 0 && (
            <div className="flex flex-col gap-12">
              <h5>Unpublished Offers</h5>
              <ScrollLoadOffers filterParams={filterParams} status={OfferStatusTypes.Unpublished} triggerAtCenter>
                <div>
                  {unpublishedOffers.map((offer) => (
                    <MyOfferBox
                      key={offer.id}
                      offer={offer}
                      tradeOfferIds={tradeOfferIds}
                      offerStatus={OfferStatusTypes.Unpublished}
                      onDeleteClick={handleDeleteClick}
                    />
                  ))}
                </div>
              </ScrollLoadOffers>
            </div>
            )}
            {draftOffers.length > 0 && (
            <div className="flex flex-col gap-12">
              <h5>Drafts</h5>
              <div className="grid grid-cols-3 max-2xl:grid-cols-2 max-md:grid-cols-1 gap-4 mt-6">
                {draftOffers.map((offer) => (
                  <DraftOfferCard offer={offer} onDeleteClick={handleDeleteClick} />))}
              </div>
            </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
